import {
  Container,
  Graphics,
  Sprite,
  Text,
  Texture,
  type Ticker
} from 'pixi.js';
import type { ViewportLayout } from '../app/Viewport';
import { BaseScene, type SceneServices } from './BaseScene';
import { Button, UI_COLORS, UI_FONT_FAMILY } from '../ui';
import endingData from '../data/ending.json';

export interface EndingSceneOptions {
  onComplete?: () => void;
  showGalleryUnlockNotice?: boolean;
}

interface EndingTrack {
  id: string;
  title: string;
  bgm: string;
  durationMs: number;
  slides: string[];
}

interface EndingData {
  fadeMs: number;
  tracks: EndingTrack[];
}

const ENDING_CONFIG = endingData as EndingData;
const SLIDE_FADE_MS = 900;
const MIN_SLIDE_MS = 1800;

export class EndingScene extends BaseScene {
  private readonly background = new Graphics();
  private readonly slideLayer = new Container();
  private readonly currentSlide = new Sprite(Texture.EMPTY);
  private readonly nextSlide = new Sprite(Texture.EMPTY);
  private readonly vignette = new Graphics();
  private readonly captionPanel = new Graphics();
  private readonly trackText = new Text({
    text: '',
    style: {
      fill: UI_COLORS.ink,
      fontFamily: UI_FONT_FAMILY,
      fontSize: 22,
      fontWeight: '800'
    }
  });
  private readonly progressText = new Text({
    text: '',
    style: {
      fill: UI_COLORS.mutedInk,
      fontFamily: UI_FONT_FAMILY,
      fontSize: 16,
      fontWeight: '700'
    }
  });
  private readonly completeText = new Text({
    text: 'THE CASE FILE NEVER CLOSES',
    style: {
      fill: UI_COLORS.gold,
      fontFamily: UI_FONT_FAMILY,
      fontSize: 30,
      fontWeight: '900',
      letterSpacing: 0
    }
  });
  private readonly titleButton = new Button({
    label: 'タイトルへ',
    width: 180,
    height: 48,
    variant: 'secondary',
    onPress: () => this.finish()
  });
  private readonly unlockNoticeLayer = new Container();
  private readonly unlockNoticeBackdrop = new Graphics();
  private readonly unlockNoticePanel = new Graphics();
  private readonly unlockNoticeTitle = new Text({
    text: 'ギャラリー更新',
    style: {
      fill: UI_COLORS.gold,
      fontFamily: UI_FONT_FAMILY,
      fontSize: 34,
      fontWeight: '900'
    }
  });
  private readonly unlockNoticeBody = new Text({
    text:
      'Case 10クリア特典として、\nキャラクター差分・背景・証拠品・\nシークレット画像がギャラリーに追加されました。',
    style: {
      fill: UI_COLORS.ink,
      fontFamily: UI_FONT_FAMILY,
      fontSize: 22,
      fontWeight: '800',
      lineHeight: 34,
      wordWrap: true,
      breakWords: true,
      wordWrapWidth: 700
    }
  });
  private readonly unlockNoticeButton = new Button({
    label: 'エンディングを見る',
    width: 240,
    height: 56,
    variant: 'primary',
    onPress: () => {
      this.unlockNoticeLayer.visible = false;
      void this.startTrack(0);
    }
  });

  private layout: ViewportLayout | null = null;
  private trackIndex = 0;
  private slideIndex = -1;
  private trackElapsedMs = 0;
  private transitionElapsedMs = 0;
  private loadToken = 0;
  private audioToken = 0;
  private hasCurrentSlide = false;
  private isTransitioning = false;
  private endingComplete = false;
  private playbackStarted = false;
  private destroyed = false;

  public constructor(
    services: SceneServices,
    private readonly options: EndingSceneOptions = {}
  ) {
    super(services);
    this.currentSlide.anchor.set(0.5);
    this.nextSlide.anchor.set(0.5);
    this.nextSlide.alpha = 0;
    this.completeText.anchor.set(0.5);
  }

  protected override onStart(): void {
    this.container.addChild(this.background);
    this.slideLayer.addChild(this.currentSlide);
    this.slideLayer.addChild(this.nextSlide);
    this.container.addChild(this.slideLayer);
    this.container.addChild(this.vignette);
    this.container.addChild(this.captionPanel);
    this.container.addChild(this.trackText);
    this.container.addChild(this.progressText);
    this.container.addChild(this.completeText);
    this.container.addChild(this.titleButton);
    this.unlockNoticeLayer.addChild(
      this.unlockNoticeBackdrop,
      this.unlockNoticePanel,
      this.unlockNoticeTitle,
      this.unlockNoticeBody,
      this.unlockNoticeButton
    );
    this.container.addChild(this.unlockNoticeLayer);
    this.completeText.visible = false;
    this.unlockNoticeLayer.visible = false;
    this.resize(this.services.getLayout());
    if (this.options.showGalleryUnlockNotice) {
      this.showUnlockNotice();
      return;
    }
    void this.startTrack(0);
  }

  public override update(ticker: Ticker): void {
    if (!this.playbackStarted || this.endingComplete || ENDING_CONFIG.tracks.length === 0) {
      return;
    }

    const deltaMs = Math.max(0, ticker.deltaMS);
    this.trackElapsedMs += deltaMs;
    this.updateSlideTransition(deltaMs);

    const track = ENDING_CONFIG.tracks[this.trackIndex];
    const slideDurationMs = getSlideDurationMs(track);
    const nextSlideIndex = Math.min(
      track.slides.length - 1,
      Math.floor(this.trackElapsedMs / slideDurationMs)
    );
    if (nextSlideIndex !== this.slideIndex) {
      void this.showSlide(nextSlideIndex);
    }

    this.updateProgressText();
    if (this.trackElapsedMs >= track.durationMs) {
      this.advanceTrack();
    }
  }

  public override resize(layout: ViewportLayout): void {
    this.layout = layout;
    this.drawBackground(layout.baseWidth, layout.baseHeight);
    this.fitSlide(this.currentSlide);
    this.fitSlide(this.nextSlide);

    const panelWidth = Math.min(560, layout.safeArea.width - 220);
    const panelHeight = 72;
    const panelX = layout.safeArea.x;
    const panelY = layout.safeArea.y + layout.safeArea.height - panelHeight;
    this.captionPanel.clear();
    this.captionPanel
      .roundRect(panelX, panelY, panelWidth, panelHeight, 8)
      .fill({ color: UI_COLORS.black, alpha: 0.58 })
      .stroke({ color: UI_COLORS.gold, width: 2, alpha: 0.62 });
    this.captionPanel.rect(panelX + 14, panelY + 12, 4, panelHeight - 24).fill({
      color: UI_COLORS.red,
      alpha: 0.9
    });
    this.trackText.position.set(panelX + 32, panelY + 12);
    this.progressText.position.set(panelX + 34, panelY + 43);
    this.titleButton.position.set(
      layout.safeArea.x + layout.safeArea.width - 180,
      layout.safeArea.y + layout.safeArea.height - 58
    );
    this.completeText.position.set(layout.baseWidth / 2, layout.baseHeight / 2);
    this.layoutUnlockNotice(layout);
  }

  protected override onDestroy(): void {
    this.destroyed = true;
    this.audioToken += 1;
  }

  private async startTrack(index: number): Promise<void> {
    const track = ENDING_CONFIG.tracks[index];
    if (!track) {
      this.completeEnding();
      return;
    }

    this.playbackStarted = true;
    this.trackIndex = index;
    this.slideIndex = -1;
    this.trackElapsedMs = 0;
    this.trackText.text = `ENDING ${index + 1} / ${track.title}`;
    this.updateProgressText();
    await this.showSlide(0, !this.hasCurrentSlide);
    const audioToken = ++this.audioToken;
    void this.services.getAudio().playBgm(track.bgm, {
      fadeMs: ENDING_CONFIG.fadeMs,
      loop: false,
      onEnd: () => {
        if (!this.destroyed && audioToken === this.audioToken) {
          this.advanceTrack();
        }
      }
    });
  }

  private advanceTrack(): void {
    if (this.endingComplete) {
      return;
    }

    const nextTrackIndex = this.trackIndex + 1;
    if (nextTrackIndex >= ENDING_CONFIG.tracks.length) {
      this.completeEnding();
      return;
    }

    void this.startTrack(nextTrackIndex);
  }

  private completeEnding(): void {
    if (this.endingComplete) {
      return;
    }

    this.endingComplete = true;
    this.trackText.text = 'ENDING COMPLETE';
    this.progressText.text = 'すべての事件は、まだ閉じていない。';
    this.completeText.visible = true;
  }

  private finish(): void {
    if (this.destroyed) {
      return;
    }

    this.endingComplete = true;
    this.audioToken += 1;
    void this.services.getAudio().stopBgm(ENDING_CONFIG.fadeMs);
    this.options.onComplete?.();
  }

  private showUnlockNotice(): void {
    this.playbackStarted = false;
    this.unlockNoticeLayer.visible = true;
    if (this.layout) {
      this.layoutUnlockNotice(this.layout);
    }
  }

  private layoutUnlockNotice(layout: ViewportLayout): void {
    const width = layout.baseWidth;
    const height = layout.baseHeight;
    this.unlockNoticeBackdrop.clear();
    this.unlockNoticeBackdrop.rect(0, 0, width, height).fill({
      color: UI_COLORS.black,
      alpha: 0.72
    });

    const panelWidth = Math.min(820, layout.safeArea.width - 80);
    const panelHeight = 288;
    const panelX = layout.baseWidth / 2 - panelWidth / 2;
    const panelY = layout.baseHeight / 2 - panelHeight / 2;
    this.unlockNoticePanel.clear();
    this.unlockNoticePanel
      .roundRect(panelX, panelY, panelWidth, panelHeight, 10)
      .fill({ color: UI_COLORS.black, alpha: 0.92 })
      .stroke({ color: UI_COLORS.gold, width: 3, alpha: 0.9 });
    this.unlockNoticePanel.rect(panelX, panelY, panelWidth, 10).fill({
      color: UI_COLORS.red,
      alpha: 0.92
    });
    this.unlockNoticePanel.rect(panelX + 30, panelY + 42, 6, 58).fill({
      color: UI_COLORS.red,
      alpha: 0.9
    });

    this.unlockNoticeTitle.position.set(panelX + 56, panelY + 42);
    this.unlockNoticeBody.style.wordWrapWidth = Math.max(320, panelWidth - 112);
    this.unlockNoticeBody.position.set(panelX + 56, panelY + 112);
    this.unlockNoticeButton.position.set(panelX + panelWidth - 280, panelY + panelHeight - 82);
  }

  private async showSlide(nextIndex: number, immediate = false): Promise<void> {
    const track = ENDING_CONFIG.tracks[this.trackIndex];
    const slideId = track?.slides[nextIndex];
    if (!slideId || nextIndex === this.slideIndex) {
      return;
    }

    this.slideIndex = nextIndex;
    const loadToken = ++this.loadToken;
    const result = await this.services.getAssets().loadImage({
      id: slideId,
      kind: 'gallery',
      path: `assets/images/gallery/${slideId}.webp`
    });
    if (this.destroyed || loadToken !== this.loadToken) {
      return;
    }

    if (immediate || !this.hasCurrentSlide) {
      this.currentSlide.texture = result.texture;
      this.currentSlide.alpha = 1;
      this.nextSlide.alpha = 0;
      this.hasCurrentSlide = true;
      this.isTransitioning = false;
      this.fitSlide(this.currentSlide);
      return;
    }

    this.nextSlide.texture = result.texture;
    this.nextSlide.alpha = 0;
    this.transitionElapsedMs = 0;
    this.isTransitioning = true;
    this.fitSlide(this.nextSlide);
  }

  private updateSlideTransition(deltaMs: number): void {
    if (!this.isTransitioning) {
      return;
    }

    this.transitionElapsedMs += deltaMs;
    const progress = Math.min(1, this.transitionElapsedMs / SLIDE_FADE_MS);
    this.currentSlide.alpha = 1 - progress;
    this.nextSlide.alpha = progress;
    if (progress < 1) {
      return;
    }

    this.currentSlide.texture = this.nextSlide.texture;
    this.currentSlide.alpha = 1;
    this.nextSlide.alpha = 0;
    this.isTransitioning = false;
    this.fitSlide(this.currentSlide);
  }

  private updateProgressText(): void {
    const track = ENDING_CONFIG.tracks[this.trackIndex];
    if (!track) {
      this.progressText.text = '';
      return;
    }

    this.progressText.text = `${this.slideIndex + 1}/${track.slides.length}  ${formatTime(
      this.trackElapsedMs
    )} / ${formatTime(track.durationMs)}`;
  }

  private fitSlide(sprite: Sprite): void {
    const layout = this.layout;
    if (!layout || sprite.texture === Texture.EMPTY) {
      return;
    }

    const textureWidth = Math.max(1, sprite.texture.width);
    const textureHeight = Math.max(1, sprite.texture.height);
    const scale = Math.max(layout.baseWidth / textureWidth, layout.baseHeight / textureHeight);
    sprite.scale.set(scale);
    sprite.position.set(layout.baseWidth / 2, layout.baseHeight / 2);
  }

  private drawBackground(width: number, height: number): void {
    this.background.clear();
    this.background.rect(0, 0, width, height).fill(UI_COLORS.black);
    this.vignette.clear();
    this.vignette.rect(0, 0, width, height).fill({ color: UI_COLORS.black, alpha: 0.08 });
    this.vignette.rect(0, 0, width, 120).fill({ color: UI_COLORS.black, alpha: 0.34 });
    this.vignette.rect(0, height - 128, width, 128).fill({ color: UI_COLORS.black, alpha: 0.38 });
  }
}

function getSlideDurationMs(track: EndingTrack): number {
  return Math.max(MIN_SLIDE_MS, track.durationMs / Math.max(1, track.slides.length));
}

function formatTime(durationMs: number): string {
  const totalSeconds = Math.max(0, Math.floor(durationMs / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}
